import type { NodeParam, NodeStruct } from '@/types/node-types';

/**
 * Plain-language copy for the Logic nodes, and a guard that keeps
 * implementation jargon out of the editor.
 *
 * The backend ships this same text on its node classes (Phase 6; see
 * agentic-backend `test_logic_catalog_copy.py`). The editor keeps a copy so
 * a frontend deployed ahead of the backend never shows the old sentences,
 * and so a catalog entry that slips through with jargon is softened rather
 * than printed as is. Keep the two in step when a node's text changes.
 */

export interface PlainNodeCopy {
  description: string;
  params: Record<string, string>;
  ports: Record<string, string>;
}

export const PLAIN_NODE_COPY: Record<string, PlainNodeCopy> = {
  switch_node: {
    description:
      'Routes a value to one named branch: cases are checked in order and the first one whose condition holds wins; if none does, the run fails or takes the default branch you name. Build cases from a field, an operator and a value in the panel; wire each branch from its labelled handle. No prompt, no code, no network call.',
    params: {
      value: 'The value to route on, from the node connected to this input.',
      cases:
        'The branches, in order: each has a name and a condition on the incoming value; the first one that holds is taken. Up to 128 cases.',
      null_behavior:
        'What happens when the incoming value is missing (null): fail the run, or take the default branch (which must then be named).',
      default_label:
        'The branch to take when no case matches. Wire it from the handle of the same name on the node.',
    },
    ports: {
      selected_case:
        'The name of the branch that was taken. Branches themselves are wired from the labelled handles on the node.',
    },
  },
  validate_node: {
    description:
      'Checks one value against the shape you describe (required fields, types, allowed values, ranges) and the rules you write (field, operator, value). Either stops the run when a check fails, or passes the value through with is_valid and the list of failed checks so a Switch or a loop can act on it. No prompt, no code.',
    params: {
      value: 'The value to check, from the node connected to this input.',
      schema:
        'The shape the value must have: which fields are required, their types, allowed values, and number or length ranges. Build it from a sample in the panel, or leave it empty and use rules only.',
      assertions:
        'Rules the value must satisfy, each with a short code and the message shown when it fails. Build each from a field, an operator and a value.',
      mode: "'fail' stops the run when a check fails; 'collect' passes the value through with is_valid and the failed checks so the next node decides.",
      error_code:
        'A short code recorded when the run is stopped by a failed check.',
      error_message:
        'The message shown when the run is stopped by a failed check.',
      config_version: "Internal: the version of this node's settings format.",
    },
    ports: {
      value: 'The same value, passed through (collect mode only).',
      is_valid:
        "True when every check passed; connect it to a Switch or a loop's judge input.",
      errors:
        'The checks that failed, each with its code, message and the field it concerns.',
    },
  },
  classify_decide_node: {
    description:
      'Asks a model to pick exactly one of the labels you name, following the criteria you write, and returns that label with its reasoning. Start from a preset (yes/no gate, triage) or name your own labels; connect the Decision output to a Switch to route on it, or use this node as a Repeat Until judge. It calls no tools and changes nothing.',
    params: {
      decision_preset:
        "A starting point: 'yes/no gate' or 'triage' fills in labels and a criteria template you can edit; 'custom' leaves them to you. Only the labels and criteria affect the decision.",
      subject:
        'What to decide about: text or JSON from the connected node. Files are not accepted; extract their text first.',
      context:
        'Optional background the model should keep in mind while deciding.',
      profile:
        "Optional preferences the decision should respect (for example a fund's thesis). If you version profiles, note the version below.",
      evidence:
        'Optional supporting material the model may cite in its reasoning.',
      decision_labels:
        'The possible answers, 2 to 32 of them, as lower_snake_case words. Exactly one is returned.',
      criteria:
        'How to decide: what makes the subject each label, in plain words.',
      system_instructions:
        'Optional extra instructions for the model (tone, edge cases). They cannot give it tools or change what it returns.',
      profile_version:
        "If you version preference profiles, the version used here; it is recorded with every decision. Otherwise 'unversioned'.",
      model: 'The model that makes the decision.',
      temperature:
        'How much the model may vary between runs; keep it low for consistent decisions.',
      max_tokens: 'The most the model may write per decision.',
      repair_attempts:
        "How many times to ask again if the model's answer is not in the expected form (0 or 1).",
      cost_budget_usd:
        'The most one decision may cost; the node refuses to run above it.',
      uncertain_label:
        'One of the labels to answer with when the model is not confident, so a Switch can send doubtful cases to a person.',
      uncertain_threshold:
        'Below this confidence (0 to 1) the node answers with the fallback label instead.',
    },
    ports: {
      subject: 'What to decide about: text or JSON. Files are not accepted.',
      context: 'Optional background for the decision.',
      profile: 'Optional preferences the decision should respect.',
      evidence: 'Optional supporting material; extract text from files first.',
      decision:
        'The label that was chosen. Connect it to a Switch to route on it.',
      result:
        'The full decision: label, reasoning, confidence and the settings used.',
      metadata: 'Which model answered, how long it took and what it used.',
    },
  },
  union_collect_node: {
    description:
      "Gathers the outputs of several nodes into one list, in connection order: 'collect' keeps one item per connection, 'union' spreads incoming lists out one level. Can drop empty values and remove repeats by value or by one field. Plain values only, not files.",
    params: {
      values: 'The values to gather, one connection each, in connection order.',
      mode: "'collect' keeps one item per connection ([1,2] and 3 become [[1,2],3]); 'union' spreads incoming lists out one level ([1,2] and 3 become [1,2,3]).",
      distinct_mode:
        'Remove repeats: by the whole value, or by one field (set the key path below). The first occurrence is kept.',
      null_handling: 'Keep empty (null) values as items, or drop them.',
      key_path:
        'The field that decides whether two items are the same, written as a path like /id or /company/name. Needed only when removing repeats by key.',
      config_version: "Internal: the version of this node's settings format.",
    },
    ports: {
      items: 'The gathered list, in connection order.',
      input_count: 'How many values came in.',
      output_count:
        'How many items went out, after dropping empties and repeats.',
      duplicates_removed: 'How many repeats were removed.',
      mode: 'Which mode ran: collect or union.',
      digest: 'A fingerprint of this result, for audit.',
    },
  },
  reduce_node: {
    description:
      'Folds a list into one value with one operation: count, sum, average, min, max, any, all, first or last non-empty, join text, remove repeats, or group by a field. Numbers come back as exact text so nothing is rounded. For anything else, use a code step.',
    params: {
      items: 'The list to fold, from the node connected to this input.',
      operation:
        'How to fold the list into one value: count, sum, average, min, max, any, all, first or last non-empty, join text, remove repeats, or group by a field. For anything else, use a code step.',
      key_path:
        'The field to group or de-duplicate by, written as a path like /id or /company/name.',
      separator:
        'Text placed between items when joining text (leave empty to join lists).',
      config_version: "Internal: the version of this node's settings format.",
    },
    ports: {
      value:
        'The folded result. Numbers arrive as text (for example "10") so nothing is rounded; the editor shows them as numbers.',
      input_count: 'How many items were folded.',
      operation: 'Which operation ran.',
      digest: 'A fingerprint of this result, for audit.',
    },
  },
  map_node: {
    description:
      'Runs one step once for every item in a list, several at a time, and returns the outcomes in input order with a count of what finished and what failed. Choose the step, the limits and what to do when an item fails in the panel.',
    params: {
      items: 'The list to go through; each entry becomes one run of the step.',
      map_config:
        'Which step runs per item, how many at a time, and what to do when one fails. Set in the panel.',
    },
    ports: {
      results:
        'One outcome per item, in input order: its value, or why it failed.',
      counts: 'How many items came in, finished and failed.',
      failure_mode:
        'Whether the run stopped at the first failure or ran every item.',
      digest: 'A fingerprint of this result, for audit.',
      provenance: 'Which step version ran and with which settings, for audit.',
    },
  },
  map_item_node: {
    description:
      'The step a Map / For Each runs for each item: returns the current item, on its own or together with its position in the list.',
    params: {
      item: 'The current item; the Map node fills this in for each run.',
      index:
        "The item's position in the list, starting at 0; filled in by the Map node.",
      projection: 'Return the item on its own, or together with its position.',
    },
    ports: {
      result: 'The current item, or the item together with its position.',
    },
  },
  repeat_until_node: {
    description:
      "Runs a step again until a judge says it is good, passing the judge's verdict back into the step each time, and stops after at most N attempts (10 at most). Pick the step and the judge in the panel; the dashed repeat edge on the canvas shows what repeats. Emits the final value, how many attempts ran and why it stopped.",
    params: {
      input:
        "Connect the judge's output here (a Validate, Classify or Jev node after the step). The loop repeats the part between the step and the judge.",
      repeat_config:
        'Which step repeats, which judge decides, the stop condition, the attempt limit and what is fed back. Set in the panel.',
    },
    ports: {
      final_value: "The step's output from the last attempt.",
      satisfied: 'True when the judge was satisfied before the attempt limit.',
      exit_reason:
        "Why the loop stopped: 'satisfied' or 'max_iterations_exhausted'. Connect to a Switch to send exhausted runs to a person.",
      iterations:
        "One record per attempt: the judge's verdict and the feedback that was passed back.",
      iteration_count: 'How many attempts ran.',
      digest: 'A fingerprint of this result, for audit.',
      provenance:
        'Which step and judge versions ran, with which settings, and why it stopped; for audit.',
    },
  },
};

/**
 * Words and shapes that describe how the platform is built, not what a node
 * does: flag and env names, the expression-tree jargon, standards numbers,
 * hashing, internal version words, module and class names.
 */
export const JARGON = new RegExp(
  [
    'TEMPORAL_[A-Z_]*',
    '[A-Z][A-Z0-9]+_ENABLED',
    '\\bAST\\b',
    '\\bRFC ?\\d+',
    'JSON Pointer',
    '\\bdigests?\\b',
    'contract version',
    'WorkflowIR',
    'pseudo[- ]cycle',
    'canvas-visible',
    '\\bTemporal\\b',
    '\\bidempoten',
    '\\b[A-Z][a-z]+Node\\b',
    '\\bnodes\\.[a-z_.]+\\b',
  ].join('|'),
);

export const hasJargon = (text: string | null | undefined): boolean =>
  Boolean(text) && JARGON.test(String(text));

/** Drop the sentences that contain jargon; keep the rest. */
export const softenJargon = (text: string): string => {
  if (!hasJargon(text)) return text;
  const kept = text
    .split(/(?<=[.!?])\s+/)
    .filter((sentence) => !hasJargon(sentence));
  return kept.join(' ').trim();
};

/**
 * The node's description for people: the plain copy when we have it, the
 * author's own description when they wrote one, else the catalog's text
 * with jargon sentences removed.
 */
export const plainNodeDescription = (
  struct: Pick<
    NodeStruct,
    'node_id' | 'description' | 'user_config_description'
  >,
): string => {
  const own = struct.user_config_description?.trim();
  if (own) return own;
  const plain = PLAIN_NODE_COPY[struct.node_id]?.description;
  if (plain) return plain;
  return softenJargon(struct.description ?? '') || struct.description || '';
};

/** The catalog description alone (never the author's), for pickers. */
export const plainCatalogDescription = (
  struct: Pick<NodeStruct, 'node_id' | 'description'>,
): string =>
  PLAIN_NODE_COPY[struct.node_id]?.description ??
  softenJargon(struct.description ?? '') ??
  '';

/** A parameter's help text for people. */
export const plainParamInfo = (
  struct: Pick<NodeStruct, 'node_id'> | undefined,
  param: Pick<NodeParam, 'field_id' | 'info'>,
): string => {
  const plain = struct
    ? PLAIN_NODE_COPY[struct.node_id]?.params[param.field_id]
    : undefined;
  if (plain) return plain;
  const info = typeof param.info === 'string' ? param.info : '';
  return softenJargon(info);
};

/** A port's help text for people. */
export const plainPortInfo = (
  struct: Pick<NodeStruct, 'node_id'> | undefined,
  port: { name: string; info?: string },
): string => {
  const plain = struct
    ? PLAIN_NODE_COPY[struct.node_id]?.ports[port.name]
    : undefined;
  if (plain) return plain;
  return softenJargon(port.info ?? '');
};
